import React, { useState, useEffect, useMemo } from 'react';
import { MessageSquare, MessageCircle, Save, TestTube, Filter, Search } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import toast from 'react-hot-toast';

interface SocialAccount {
  id: string;
  platform: string;
  page_name: string;
  instagram_direct_equivalent_id?: string;
}

interface SocialSettings {
  id: string;
  account_id: string;
  mode_dm: boolean;
  dm_webhook_url: string | null;
  dm_timeout_ms: number;
  dm_max_retries: number;
  mode_comments: boolean;
  comments_webhook_url: string | null;
  poll_cron: string;
  posts: string[];
  skip_if_replied: boolean;
  reply_delay_minutes: number;
  human_takeover_enabled: boolean;
  human_takeover_release_hours: number | null;
}

export function SocialAutomations() {
  const [accounts, setAccounts] = useState<SocialAccount[]>([]);
  const [selectedAccountId, setSelectedAccountId] = useState<string>('');
  const [platformFilter, setPlatformFilter] = useState<string>('all');
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [settings, setSettings] = useState<Partial<SocialSettings>>({
    mode_dm: false,
    dm_webhook_url: '',
    dm_timeout_ms: 8000,
    dm_max_retries: 3,
    mode_comments: false,
    comments_webhook_url: '',
    poll_cron: '*/15 * * * *',
    posts: [],
    skip_if_replied: true,
    reply_delay_minutes: 5,
    human_takeover_enabled: true,
    human_takeover_release_hours: null,
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadAccounts();
  }, []);

  useEffect(() => {
    if (selectedAccountId) {
      loadSettings(selectedAccountId);
    }
  }, [selectedAccountId]);

  useEffect(() => {
    if (accounts.length === 0) return;

    let filtered = accounts;

    if (platformFilter !== 'all') {
      filtered = filtered.filter(acc => acc.platform === platformFilter);
    }

    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(acc => acc.page_name.toLowerCase().includes(query));
    }

    if (filtered.length > 0 && selectedAccountId) {
      const isSelectedInFiltered = filtered.some(acc => acc.id === selectedAccountId);
      if (!isSelectedInFiltered) {
        setSelectedAccountId(filtered[0].id);
      }
    }
  }, [platformFilter, searchQuery, accounts]);

  const loadAccounts = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data, error } = await supabase
        .from('social_accounts')
        .select('id, platform, page_name, instagram_direct_equivalent_id')
        .eq('owner_user_id', user.id)
        .eq('status', 'active');

      if (error) throw error;

      const sortedData = (data || []).sort((a, b) => a.page_name.localeCompare(b.page_name));
      setAccounts(sortedData);
      if (sortedData.length > 0 && !selectedAccountId) {
        setSelectedAccountId(sortedData[0].id);
      }
    } catch (error) {
      console.error('Error loading accounts:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadSettings = async (accountId: string) => {
    try {
      const { data, error } = await supabase
        .from('social_settings')
        .select('*')
        .eq('account_id', accountId)
        .maybeSingle();

      if (error) throw error;

      if (data) {
        setSettings({
          ...data,
          posts: Array.isArray(data.posts) ? data.posts : [],
        });
      } else {
        setSettings({
          mode_dm: false,
          dm_webhook_url: '',
          dm_timeout_ms: 8000,
          dm_max_retries: 3,
          mode_comments: false,
          comments_webhook_url: '',
          poll_cron: '*/15 * * * *',
          reply_delay_minutes: 5,
          posts: [],
          skip_if_replied: true,
          human_takeover_enabled: true,
          human_takeover_release_hours: null,
        });
      }
    } catch (error) {
      console.error('Error loading settings:', error);
    }
  };

  const handleSave = async () => {
    if (!selectedAccountId) return;

    setSaving(true);
    try {
      const { error } = await supabase
        .from('social_settings')
        .upsert({
          account_id: selectedAccountId,
          ...settings,
        }, {
          onConflict: 'account_id'
        });

      if (error) throw error;

      toast.success('Ustawienia zostały zapisane');
    } catch (error) {
      console.error('Error saving settings:', error);
      toast.error('Nie udało się zapisać ustawień');
    } finally {
      setSaving(false);
    }
  };

  const filteredAccounts = useMemo(() => {
    let filtered = accounts;

    if (platformFilter !== 'all') {
      filtered = filtered.filter(acc => acc.platform === platformFilter);
    }

    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(acc =>
        acc.page_name.toLowerCase().includes(query)
      );
    }

    return filtered;
  }, [accounts, platformFilter, searchQuery]);

  const platformCounts = useMemo(() => {
    return accounts.reduce((acc, account) => {
      acc[account.platform] = (acc[account.platform] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);
  }, [accounts]);

  const selectedAccount = accounts.find(acc => acc.id === selectedAccountId);
  const isInstagramBusiness = selectedAccount?.platform === 'instagram';
  const isYouTube = selectedAccount?.platform === 'youtube';

  const getPlatformLabel = (platform: string) => {
    const labels: Record<string, string> = {
      'facebook': 'Facebook',
      'instagram': 'Instagram Business',
      'instagram_direct': 'Instagram Direct',
      'youtube': 'YouTube',
      'tiktok': 'TikTok',
      'x': 'X (Twitter)',
      'linkedin': 'LinkedIn',
      'google_business': 'Google Business'
    };
    return labels[platform] || platform;
  };

  const handleTestWebhook = async (type: 'dm' | 'comment') => {
    const webhookUrl = type === 'dm' ? settings.dm_webhook_url : settings.comments_webhook_url;

    if (!webhookUrl) {
      toast.error('Podaj URL webhooka');
      return;
    }

    const testPayload = type === 'dm' ? {
      type: 'dm.inbound',
      platform: 'facebook',
      account_id: selectedAccountId,
      conversation_id: 'test_123',
      message_id: 'test_msg_123',
      from: { id: 'test_user', name: 'Test User' },
      text: 'To jest testowa wiadomość',
      timestamp: new Date().toISOString(),
      meta: { page_id: 'test_page' },
    } : {
      type: 'comment.inbound',
      platform: 'facebook',
      account_id: selectedAccountId,
      post_id: 'test_post_123',
      comment_id: 'test_comment_123',
      from: { id: 'test_user', name: 'Test User' },
      text: 'To jest testowy komentarz',
      timestamp: new Date().toISOString(),
    };

    try {
      const response = await fetch(webhookUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(testPayload),
      });

      if (response.ok) {
        const data = await response.json();
        toast.success(`Webhook odpowiedział: ${JSON.stringify(data)}`);
      } else {
        toast.error(`Webhook zwrócił błąd: ${response.status}`);
      }
    } catch (error) {
      toast.error('Nie udało się połączyć z webhookiem');
    }
  };

  if (loading) {
    return <div className="text-white">Ładowanie...</div>;
  }

  if (accounts.length === 0) {
    return (
      <div className="bg-gray-800 rounded-lg p-12 text-center">
        <h3 className="text-xl font-semibold text-white mb-2">
          Brak połączonych kont
        </h3>
        <p className="text-gray-400">
          Najpierw połącz konto Facebook lub Instagram w zakładce "Konta"
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-white mb-1">Automatyzacje</h2>
        <p className="text-gray-400">
          Konfiguruj automatyczne odpowiedzi na wiadomości i komentarze
        </p>
      </div>

      <div className="bg-gray-800 rounded-lg p-4 border border-gray-700">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="flex items-center space-x-2 text-sm font-medium text-gray-300 mb-2">
              <Filter className="w-4 h-4" />
              <span>Filtruj według platformy</span>
            </label>
            <select
              value={platformFilter}
              onChange={(e) => setPlatformFilter(e.target.value)}
              className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-[#7B4BFF]"
            >
              <option value="all">Wszystkie platformy ({accounts.length})</option>
              {Object.entries(platformCounts).map(([platform, count]) => (
                <option key={platform} value={platform}>
                  {getPlatformLabel(platform)} ({count})
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="flex items-center space-x-2 text-sm font-medium text-gray-300 mb-2">
              <Search className="w-4 h-4" />
              <span>Wyszukaj konto</span>
            </label>
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Wpisz nazwę konta..."
              className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-[#7B4BFF]"
            />
          </div>
        </div>

        {searchQuery && filteredAccounts.length === 0 && (
          <div className="mt-4 text-center text-gray-400 text-sm">
            Nie znaleziono kont pasujących do &quot;{searchQuery}&quot;
          </div>
        )}
      </div>

      {filteredAccounts.length === 0 ? (
        <div className="bg-gray-800 rounded-lg p-12 text-center border border-gray-700">
          <p className="text-gray-400">
            Brak kont pasujących do wybranych filtrów
          </p>
        </div>
      ) : (
        <>
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Wybierz konto
              {filteredAccounts.length !== accounts.length && (
                <span className="ml-2 text-gray-500">
                  (wyświetlono {filteredAccounts.length} z {accounts.length})
                </span>
              )}
            </label>
            <select
              value={selectedAccountId}
              onChange={(e) => setSelectedAccountId(e.target.value)}
              className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-[#7B4BFF]"
            >
              {filteredAccounts.map((account) => (
                <option key={account.id} value={account.id}>
                  {account.page_name} ({getPlatformLabel(account.platform)})
                </option>
              ))}
            </select>
          </div>

          <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
        <div className="flex items-center space-x-3 mb-4">
          <MessageSquare className="w-6 h-6 text-[#7B4BFF]" />
          <h3 className="text-xl font-semibold text-white">
            Wiadomości prywatne (DM)
          </h3>
        </div>

        {isYouTube ? (
          <div className="bg-yellow-900/20 border border-yellow-700/50 rounded-lg p-4">
            <p className="text-yellow-200 text-sm mb-2">
              YouTube nie obsługuje wiadomości prywatnych przez API.
            </p>
            <p className="text-yellow-200/80 text-sm">
              YouTube obsługuje tylko komentarze pod filmami. Wiadomości prywatne nie są dostępne poprzez webhoki ani API.
            </p>
          </div>
        ) : isInstagramBusiness && !selectedAccount?.instagram_direct_equivalent_id ? (
          <div className="bg-yellow-900/20 border border-yellow-700/50 rounded-lg p-4">
            <p className="text-yellow-200 text-sm mb-2">
              Konta Instagram Business nie mogą odbierać wiadomości prywatnych przez webhoki.
            </p>
            <p className="text-yellow-200/80 text-sm">
              Aby obsługiwać wiadomości prywatne Instagram, połącz to konto również jako Instagram Direct w zakładce "Konta".
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="flex items-center">
              <input
                type="checkbox"
                id="mode_dm"
                checked={settings.mode_dm || false}
                onChange={(e) => setSettings({ ...settings, mode_dm: e.target.checked })}
                className="w-4 h-4 text-[#7B4BFF] bg-gray-700 border-gray-600 rounded focus:ring-[#7B4BFF]"
              />
              <label htmlFor="mode_dm" className="ml-2 text-sm text-gray-300">
                Przekazuj wszystkie DM do mojego webhooka
              </label>
            </div>

            {settings.mode_dm && (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  URL webhooka
                </label>
                <input
                  type="url"
                  value={settings.dm_webhook_url || ''}
                  onChange={(e) => setSettings({ ...settings, dm_webhook_url: e.target.value })}
                  placeholder="https://twoj-webhook.com/endpoint"
                  className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-[#7B4BFF]"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Timeout (ms)
                  </label>
                  <input
                    type="number"
                    value={settings.dm_timeout_ms || 8000}
                    onChange={(e) => setSettings({ ...settings, dm_timeout_ms: parseInt(e.target.value) })}
                    className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-[#7B4BFF]"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Maksymalna liczba prób
                  </label>
                  <input
                    type="number"
                    value={settings.dm_max_retries || 3}
                    onChange={(e) => setSettings({ ...settings, dm_max_retries: parseInt(e.target.value) })}
                    className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-[#7B4BFF]"
                  />
                </div>
              </div>

              <div className="border-t border-gray-700 pt-4 space-y-3">
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="human_takeover_enabled"
                    checked={settings.human_takeover_enabled ?? true}
                    onChange={(e) => setSettings({ ...settings, human_takeover_enabled: e.target.checked })}
                    className="w-4 h-4 text-[#7B4BFF] bg-gray-700 border-gray-600 rounded focus:ring-[#7B4BFF]"
                  />
                  <label htmlFor="human_takeover_enabled" className="ml-2 text-sm text-gray-300">
                    Pracownik przejmuje rozmowę — bot milknie
                  </label>
                </div>
                <p className="text-xs text-gray-400">
                  Gdy ktoś z zespołu odpisze klientowi ze Skrzynki odbiorczej, bot przestaje
                  odpowiadać w tym wątku. Zablokowane rozmowy widać w zakładce Auto DM, tam
                  też można je odblokować.
                </p>

                {(settings.human_takeover_enabled ?? true) && (
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Automatyczne zdjęcie blokady
                    </label>
                    <select
                      value={settings.human_takeover_release_hours ?? ''}
                      onChange={(e) => setSettings({
                        ...settings,
                        human_takeover_release_hours: e.target.value === '' ? null : parseInt(e.target.value),
                      })}
                      className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-[#7B4BFF]"
                    >
                      <option value="">Nigdy (tylko ręcznie)</option>
                      <option value="6">Po 6 godzinach</option>
                      <option value="24">Po 24 godzinach</option>
                      <option value="72">Po 3 dniach</option>
                      <option value="168">Po 7 dniach</option>
                    </select>
                    <p className="text-xs text-gray-400 mt-1">
                      Licząc od ostatniej wiadomości pracownika w tej rozmowie.
                    </p>
                  </div>
                )}
              </div>

              <button
                onClick={() => handleTestWebhook('dm')}
                className="flex items-center space-x-2 px-4 py-2 bg-gray-700 text-white rounded-lg hover:bg-gray-600 transition-colors"
              >
                <TestTube className="w-4 h-4" />
                <span>Wyślij testową wiadomość</span>
              </button>
            </>
            )}
          </div>
        )}
      </div>

      <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
        <div className="flex items-center space-x-3 mb-4">
          <MessageCircle className="w-6 h-6 text-[#7B4BFF]" />
          <h3 className="text-xl font-semibold text-white">
            Komentarze
          </h3>
        </div>

          <div className="space-y-4">
            <div className="flex items-center">
              <input
                type="checkbox"
                id="mode_comments"
                checked={settings.mode_comments || false}
                onChange={(e) => setSettings({ ...settings, mode_comments: e.target.checked })}
                className="w-4 h-4 text-[#7B4BFF] bg-gray-700 border-gray-600 rounded focus:ring-[#7B4BFF]"
              />
              <label htmlFor="mode_comments" className="ml-2 text-sm text-gray-300">
                Pobieraj komentarze i przekazuj do webhooka
              </label>
            </div>

            {settings.mode_comments && (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  URL webhooka dla komentarzy
                </label>
                <input
                  type="url"
                  value={settings.comments_webhook_url || ''}
                  onChange={(e) => setSettings({ ...settings, comments_webhook_url: e.target.value })}
                  placeholder="https://twoj-webhook.com/comments"
                  className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-[#7B4BFF]"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Częstotliwość pobierania
                  </label>
                  <select
                    value={settings.poll_cron || '*/15 * * * *'}
                    onChange={(e) => setSettings({ ...settings, poll_cron: e.target.value })}
                    className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-[#7B4BFF]"
                  >
                    <option value="*/5 * * * *">Co 5 minut</option>
                    <option value="*/15 * * * *">Co 15 minut</option>
                    <option value="*/30 * * * *">Co 30 minut</option>
                    <option value="0 * * * *">Co godzinę</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Opóźnienie odpowiedzi
                  </label>
                  <select
                    value={settings.reply_delay_minutes || 5}
                    onChange={(e) => setSettings({ ...settings, reply_delay_minutes: parseInt(e.target.value) })}
                    className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-[#7B4BFF]"
                  >
                    <option value="0">Natychmiast</option>
                    <option value="5">5 minut</option>
                    <option value="10">10 minut</option>
                    <option value="15">15 minut</option>
                  </select>
                  <p className="text-xs text-gray-400 mt-1">
                    Czas oczekiwania przed wysłaniem odpowiedzi (bardziej naturalnie)
                  </p>
                </div>
              </div>

              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="skip_if_replied"
                  checked={settings.skip_if_replied || false}
                  onChange={(e) => setSettings({ ...settings, skip_if_replied: e.target.checked })}
                  className="w-4 h-4 text-[#7B4BFF] bg-gray-700 border-gray-600 rounded focus:ring-[#7B4BFF]"
                />
                <label htmlFor="skip_if_replied" className="ml-2 text-sm text-gray-300">
                  Pomijaj komentarze z naszą odpowiedzią
                </label>
              </div>

              <button
                onClick={() => handleTestWebhook('comment')}
                className="flex items-center space-x-2 px-4 py-2 bg-gray-700 text-white rounded-lg hover:bg-gray-600 transition-colors"
              >
                <TestTube className="w-4 h-4" />
                <span>Wyślij testowy komentarz</span>
              </button>
            </>
            )}
          </div>
      </div>

      <div className="flex justify-end">
        <button
          onClick={handleSave}
          disabled={saving}
          className="flex items-center space-x-2 px-6 py-3 bg-[#7B4BFF] text-white rounded-lg hover:bg-[#6A3FEF] disabled:opacity-50 transition-colors"
        >
          {saving ? (
            <>
              <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
              <span>Zapisywanie...</span>
            </>
          ) : (
            <>
              <Save className="w-5 h-5" />
              <span>Zapisz ustawienia</span>
            </>
          )}
        </button>
      </div>
        </>
      )}
    </div>
  );
}
